// src/components/orders/OrdersFilter.jsx
import React, { useState } from 'react';
import Button from '../common/Button';

const OrdersFilter = ({ filters, onFilterChange }) => {
  const [searchInput, setSearchInput] = useState(filters.search || '');
  const [isOpen, setIsOpen] = useState(false);
  const [startDate, setStartDate] = useState(filters.dateRange?.startDate || '');
  const [endDate, setEndDate] = useState(filters.dateRange?.endDate || '');
  
  // Handle search input
  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };
  
  // Handle search submit
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    onFilterChange('search', searchInput);
  };
  
  // Handle status change
  const handleStatusChange = (e) => {
    onFilterChange('status', e.target.value);
  };
  
  // Handle date range change
  const handleDateRangeChange = () => {
    if (startDate && endDate) {
      onFilterChange('dateRange', { startDate, endDate });
    } else {
      onFilterChange('dateRange', null);
    }
  };
  
  // Clear all filters
  const handleClearFilters = () => {
    onFilterChange('search', '');
    onFilterChange('status', '');
    onFilterChange('dateRange', null);
    setSearchInput('');
    setStartDate('');
    setEndDate('');
    setIsOpen(false);
  };
  
  // Toggle filters panel
  const toggleFilters = () => {
    setIsOpen(!isOpen);
  };
  
  return (
    <div className="bg-white shadow rounded-md p-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-2 sm:space-y-0">
        {/* Search Bar */}
        <form onSubmit={handleSearchSubmit} className="w-full sm:w-96">
          <div className="relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
              placeholder="Search orders by ID, customer..."
              value={searchInput}
              onChange={handleSearchChange}
            />
            <div className="absolute inset-y-0 right-0 flex items-center">
              <Button 
                type="submit" 
                className="h-full rounded-l-none"
              >
                Search
              </Button>
            </div>
          </div>
        </form>
        
        {/* Toggle Filters Button */}
        <button
          type="button"
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
          onClick={toggleFilters}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
          </svg>
          {isOpen ? 'Hide Filters' : 'Show Filters'}
        </button>
      </div>
      
      {/* Filters Panel */}
      {isOpen && (
        <div className="mt-4 border-t pt-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {/* Status Filter */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                Order Status
              </label>
              <select
                id="status"
                name="status"
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm rounded-md"
                value={filters.status}
                onChange={handleStatusChange}
              >
                <option value="">All Statuses</option>
                <option value="Pending">Pending</option>
                <option value="Confirmed">Confirmed</option>
                <option value="Processing">Processing</option>
                <option value="Shipped">Shipped</option>
                <option value="Delivered">Delivered</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
            
            {/* Date Range Filter */}
            <div>
              <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">
                Start Date
              </label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm rounded-md"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="endDate" className="block text-sm font-medium text-gray-700">
                End Date
              </label>
              <div className="flex">
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm rounded-md"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
                <Button
                  type="button"
                  variant="secondary"
                  className="ml-2 mt-1"
                  onClick={handleDateRangeChange}
                  disabled={!startDate || !endDate}
                >
                  Apply
                </Button>
              </div>
            </div>
          </div>
          
          {/* Active Filters and Clear Button */}
          <div className="mt-4 flex items-center justify-between">
            <div className="flex flex-wrap gap-2">
              {filters.search && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                  Search: {filters.search}
                  <button
                    type="button"
                    className="ml-1 text-orange-400 hover:text-orange-600"
                    onClick={() => {
                      onFilterChange('search', '');
                      setSearchInput('');
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </span>
              )}
              
              {filters.status && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                  Status: {filters.status}
                  <button
                    type="button"
                    className="ml-1 text-orange-400 hover:text-orange-600"
                    onClick={() => onFilterChange('status', '')}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </span>
              )}
              
              {filters.dateRange && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                  Date: {filters.dateRange.startDate} to {filters.dateRange.endDate}
                  <button
                    type="button"
                    className="ml-1 text-orange-400 hover:text-orange-600"
                    onClick={() => {
                      onFilterChange('dateRange', null);
                      setStartDate('');
                      setEndDate('');
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </span>
              )}
            </div>
            
            <button
              type="button"
              className="text-sm text-gray-600 hover:text-gray-900"
              onClick={handleClearFilters}
            >
              Clear all filters
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersFilter;